/**
 * WebRTC client for connecting to the Nova Sonic Python server via KVS signaling.
 * Browser acts as KVS VIEWER; Python server is KVS MASTER.
 */
import { SignalingClient, Role } from 'amazon-kinesis-video-streams-webrtc';
import { KinesisVideoClient, GetSignalingChannelEndpointCommand, DescribeSignalingChannelCommand } from '@aws-sdk/client-kinesis-video';
import { KinesisVideoSignalingClient, GetIceServerConfigCommand } from '@aws-sdk/client-kinesis-video-signaling';

export class NovaSonicWebRTCClient {
  constructor({ region, credentials, channelName, onAudioTrack, onDataMessage }) {
    this.region = region;
    this.credentials = credentials;
    this.channelName = channelName;
    this.onAudioTrack = onAudioTrack;
    this.onDataMessage = onDataMessage;
    this.peerConnection = null;
    this.signalingClient = null;
    this.localStream = null;
  }

  async connect() {
    const kinesisVideoClient = new KinesisVideoClient({ region: this.region, credentials: this.credentials });

    // Get channel ARN
    const describeRes = await kinesisVideoClient.send(new DescribeSignalingChannelCommand({ ChannelName: this.channelName }));
    const channelARN = describeRes.ChannelInfo.ChannelARN;

    // Get signaling endpoints
    const endpointRes = await kinesisVideoClient.send(new GetSignalingChannelEndpointCommand({
      ChannelARN: channelARN,
      SingleMasterChannelEndpointConfiguration: { Protocols: ['WSS', 'HTTPS'], Role: 'VIEWER' }
    }));
    const endpoints = endpointRes.ResourceEndpointList.reduce((acc, ep) => { acc[ep.Protocol] = ep.ResourceEndpoint; return acc; }, {});

    // Get ICE servers
    const signalingClient = new KinesisVideoSignalingClient({ region: this.region, credentials: this.credentials, endpoint: endpoints.HTTPS });
    const iceRes = await signalingClient.send(new GetIceServerConfigCommand({ ChannelARN: channelARN }));
    const iceServers = [
      { urls: `stun:stun.kinesisvideo.${this.region}.amazonaws.com:443` },
      ...iceRes.IceServerList.map(s => ({ urls: s.Uris, username: s.Username, credential: s.Password }))
    ];

    // Create peer connection
    this.peerConnection = new RTCPeerConnection({ iceServers });

    // Create the data channel on the VIEWER (offerer) so it is included in the
    // SDP offer and actually negotiates. (An answerer cannot add an m-line the
    // offer lacks, so a master-created channel would never open.) This channel
    // is bidirectional: we receive transcripts on it and send viewer-initiated
    // text (e.g. results-ticker clicks) back to the server.
    this.dataChannel = this.peerConnection.createDataChannel('kvsDataChannel');
    this.dataChannel.onmessage = (msg) => {
      if (this.onDataMessage) {
        try { this.onDataMessage(JSON.parse(msg.data)); } catch (e) { /* ignore non-json */ }
      }
    };

    // Get mic audio
    this.localStream = await navigator.mediaDevices.getUserMedia({ audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true }, video: false });
    this.localStream.getTracks().forEach(track => this.peerConnection.addTrack(track, this.localStream));

    // Handle remote audio from Nova Sonic
    this.peerConnection.ontrack = (event) => {
      if (event.track.kind === 'audio' && this.onAudioTrack) {
        this.onAudioTrack(event.streams[0]);
      }
    };

    // If the master also opens a channel, receive on it too — but keep our
    // own (viewer-created) channel as the send channel.
    this.peerConnection.ondatachannel = (event) => {
      event.channel.onmessage = (msg) => {
        if (this.onDataMessage) {
          try { this.onDataMessage(JSON.parse(msg.data)); } catch (e) { /* ignore non-json */ }
        }
      };
    };

    // Set up KVS signaling as VIEWER
    this.signalingClient = new SignalingClient({
      channelARN,
      channelEndpoint: endpoints.WSS,
      role: Role.VIEWER,
      region: this.region,
      credentials: this.credentials,
      clientId: 'avatar-browser-' + Date.now()
    });

    this.signalingClient.on('open', async () => {
      const offer = await this.peerConnection.createOffer({ offerToReceiveAudio: true });
      await this.peerConnection.setLocalDescription(offer);
      this.signalingClient.sendSdpOffer(offer);
    });

    this.signalingClient.on('sdpAnswer', async (answer) => {
      await this.peerConnection.setRemoteDescription(answer);
    });

    this.signalingClient.on('iceCandidate', async (candidate) => {
      await this.peerConnection.addIceCandidate(candidate);
    });

    this.peerConnection.onicecandidate = ({ candidate }) => {
      if (candidate) this.signalingClient.sendIceCandidate(candidate);
    };

    this.signalingClient.open();

    return new Promise((resolve) => {
      this.peerConnection.onconnectionstatechange = () => {
        if (this.peerConnection.connectionState === 'connected') resolve();
      };
      // Timeout fallback
      setTimeout(resolve, 10000);
    });
  }

  muteMic(muted) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(t => { t.enabled = !muted; });
    }
  }

  // Send a viewer-initiated text prompt to the server (e.g. ticker click).
  // The server injects it as a USER turn so the pundit responds by voice.
  sendUserText(text) {
    if (this.dataChannel && this.dataChannel.readyState === "open") {
      this.dataChannel.send(JSON.stringify({ type: "userText", text }));
      return true;
    }
    console.warn("Data channel not open; cannot send user text");
    return false;
  }

  disconnect() {
    if (this.signalingClient) this.signalingClient.close();
    if (this.peerConnection) this.peerConnection.close();
    if (this.localStream) this.localStream.getTracks().forEach(t => t.stop());
  }
}
